import React, { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { authApi } from '../api/authApi';
import AuthLayout from '../components/common/AuthLayout';
import MarkdownModal from '../components/common/MarkdownModal';
import { showErrorToast } from '../lib/toast';

const inputClassName =
  'block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors';

const labelClassName = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

const MIN_USE_CASE_LENGTH = 20;

const USE_CASE_PLACEHOLDER =
  'What problem are you trying to solve? Who will use it? What data or workflows do you want to manage with manteo.ai?';

const REQUEST_ACCESS_INTRO =
  "We'd love to learn what you're working on. Tell us a bit about yourself below — most requests are reviewed within the first few hours.";

const REQUEST_ACCESS_LIMITED_NOTICE =
  "We're welcoming selected users now. Tell us a bit about your use case and we'll review your request right away.";

const REQUEST_ACCESS_SUCCESS =
  "You're all set. We review most requests within the first few hours and will email you as soon as there's an update.";

const AccessRequestPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prefilledEmail = location.state?.email || searchParams.get('email') || '';

  const [form, setForm] = useState({
    full_name: '',
    email: prefilledEmail,
    company: '',
    position: '',
    use_case: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.use_case.trim().length < MIN_USE_CASE_LENGTH) {
      setError(`Please provide at least ${MIN_USE_CASE_LENGTH} characters describing your use case.`);
      return;
    }

    setIsLoading(true);

    try {
      await authApi.submitAccessRequest(form);
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : err?.message || 'Could not submit your request. Please try again.';
      setError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const footer = (
    <p className="text-center text-[13px] text-gray-400 dark:text-gray-500">
      <button
        type="button"
        onClick={() => setTermsModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Terms
      </button>
      {' · '}
      <button
        type="button"
        onClick={() => setPrivacyModalOpen(true)}
        className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
      >
        Privacy
      </button>
    </p>
  );

  return (
    <AuthLayout wide footer={footer}>
      <div className="space-y-2">
        <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
          {submitted ? "You're on the list" : 'Request access'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {submitted ? REQUEST_ACCESS_SUCCESS : REQUEST_ACCESS_INTRO}
        </p>
        {!submitted && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            {REQUEST_ACCESS_LIMITED_NOTICE}
          </div>
        )}
      </div>

      <div className="mt-8">
        {submitted ? (
          <div className="space-y-6">
            <div className="flex flex-col items-center gap-3 p-6 bg-emerald-50/80 dark:bg-emerald-950/40 backdrop-blur-sm border border-emerald-200 dark:border-emerald-900/50 rounded-2xl text-center">
              <CheckCircle2 size={36} className="text-emerald-600 dark:text-emerald-400" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Thanks — we&apos;ve got your request for{' '}
                <span className="font-semibold text-gray-900 dark:text-white">{form.email}</span>.
              </p>
            </div>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="full-name" className={labelClassName}>
                  Name
                </label>
                <input
                  id="full-name"
                  type="text"
                  required
                  value={form.full_name}
                  onChange={handleChange('full_name')}
                  className={inputClassName}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className={labelClassName}>
                  Work email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                    className={`${inputClassName} pl-10`}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="company" className={labelClassName}>
                  Company
                </label>
                <input
                  id="company"
                  type="text"
                  required
                  value={form.company}
                  onChange={handleChange('company')}
                  className={inputClassName}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="role" className={labelClassName}>
                  Role
                </label>
                <input
                  id="role"
                  type="text"
                  required
                  value={form.position}
                  onChange={handleChange('position')}
                  className={inputClassName}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="use-case" className={labelClassName}>
                  Tell us a bit about your use case
                </label>
                <textarea
                  id="use-case"
                  required
                  minLength={MIN_USE_CASE_LENGTH}
                  rows={4}
                  value={form.use_case}
                  onChange={handleChange('use_case')}
                  placeholder={USE_CASE_PLACEHOLDER}
                  className={`${inputClassName} resize-none`}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {form.use_case.trim().length < MIN_USE_CASE_LENGTH
                    ? `At least ${MIN_USE_CASE_LENGTH} characters (${form.use_case.trim().length}/${MIN_USE_CASE_LENGTH})`
                    : `${form.use_case.trim().length} characters`}
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || form.use_case.trim().length < MIN_USE_CASE_LENGTH}
              className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send size={20} />
                  <span>Request access</span>
                </>
              )}
            </button>

            <p className="text-center pt-2 text-sm text-gray-600 dark:text-gray-400">
              Already received an invite or have a promo code?{' '}
              <Link
                to="/register"
                state={{ email: form.email }}
                className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors underline-offset-4 hover:underline"
              >
                Register here
              </Link>
            </p>
          </form>
        )}
      </div>

      <MarkdownModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        markdownUrl="/terms-of-service.md"
      />
      <MarkdownModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        markdownUrl="/privacy-policy.md"
      />
    </AuthLayout>
  );
};

export default AccessRequestPage;
